/* Buffalo Bunny "See it in your space" — modal visualizer.
   Real render path: posts the room photo + this product's pattern to the
   render service (data-render-endpoint), which calls Google "Nano Banana"
   (gemini-2.5-flash-image) server-side and returns the composited room.
   If no endpoint is set, or the call fails, it falls back to a local canvas
   mock so the feature never hard-breaks. Theme asset. */
(function () {
  'use strict';

  const $ = (id) => document.getElementById(id);
  const overlay = $('bbvOverlay');
  const trigger = $('bbvTrigger');
  if (!overlay || !trigger) return; // snippet not on this page

  // Server-side render proxy. Empty → use the local mock (no key/proxy yet).
  const RENDER_ENDPOINT = (overlay.dataset.renderEndpoint || '').trim();
  // Same service, minus the /render path — used for the phone hand-off routes
  // (/pair, /pair/:id, /m/:id). Empty when there is no real render service.
  const API_BASE = RENDER_ENDPOINT.replace(/\/render$/, '');

  // The id of the render currently on screen, so the viewer can tell us it is
  // wrong. Null when we have no id (an older Worker, or a failed render).
  let LAST_RENDER_ID = null;
  // Resolve a theme asset URL to absolute so the render service can fetch it
  // (asset_url can be relative or protocol-relative).
  const absUrl = (u) => { try { return u ? new URL(u, location.href).href : ''; } catch (e) { return u || ''; } };

  // "Is this a touch device that has its own camera?" — decides upload-here vs the
  // desktop QR hand-off. iPadOS Safari defaults to DESKTOP-CLASS browsing, where
  // `pointer:coarse` is unreliable (an iPad can report as a fine-pointer desktop),
  // which sent iPads into the same-device QR screen = a dead end. maxTouchPoints
  // catches iPads/tablets even when they masquerade as desktop, so they open the
  // uploader instead. Any touch device can upload directly, so this is safe.
  const isTouchDevice = () => {
    try {
      return (window.matchMedia && window.matchMedia('(pointer:coarse)').matches)
        || (navigator.maxTouchPoints || 0) > 1;
    } catch (e) { return false; }
  };

  // Cloudflare Turnstile (bot check). Sitekey comes from the section setting;
  // when empty no widget renders, and the Worker fail-opens until its
  // TURNSTILE_SECRET is set — so the feature degrades cleanly during rollout.
  const TS_SITEKEY = (overlay.dataset.turnstileSitekey || '').trim();
  let _tsWidgetId = null;
  function ensureTurnstile() {
    if (!TS_SITEKEY || _tsWidgetId !== null || !window.turnstile) return;
    const el = document.getElementById('bbvTurnstile');
    if (!el) return;
    try { _tsWidgetId = window.turnstile.render(el, { sitekey: TS_SITEKEY, appearance: 'interaction-only', size: 'flexible' }); } catch (e) {}
  }
  function getTurnstileToken() {
    try { return (window.turnstile && _tsWidgetId !== null) ? (window.turnstile.getResponse(_tsWidgetId) || '') : ''; } catch (e) { return ''; }
  }
  function resetTurnstile() {
    try { if (window.turnstile && _tsWidgetId !== null) window.turnstile.reset(_tsWidgetId); } catch (e) {}
  }
  // Resolve once the widget has issued a token (or after `ms`), so a fast upload
  // doesn't POST an empty token, get 403'd, and fall back to the local mock band.
  function waitForTurnstileToken(ms) {
    return new Promise((resolve) => {
      if (!TS_SITEKEY) return resolve('');       // no widget configured → nothing to wait for
      const t0 = performance.now();
      (function poll() {
        if (getTurnstileToken() || performance.now() - t0 > ms) return resolve(getTurnstileToken());
        setTimeout(poll, 150);
      })();
    });
  }

  // Always start closed — guards against a back/forward (bfcache) restore
  // showing the modal open on load.
  function ensureClosed() { overlay.classList.remove('is-open'); document.body.style.overflow = ''; }
  ensureClosed();
  window.addEventListener('pageshow', (e) => { if (e.persisted) ensureClosed(); });

  // Config — type + repeat come from the generated data map (bbv-patterns.json,
  // keyed by SKU stem); fall back to the metafield repeat / 36.
  const CONFIG = {
    productName: trigger.dataset.product || 'this design',
    repeatInches: Number(trigger.dataset.repeat || 36), // pattern repeat WIDTH (in)
    repeatHInches: Number(trigger.dataset.repeatH || 108), // pattern repeat HEIGHT (in) — roll height
    sizeLabel: trigger.dataset.size || '',              // selected variant size
    type: 'repeat',            // 'repeat' (tile) | 'mural' (fit-to-wall)
    // 'wallpaper' (paint pattern onto the wall) | 'pillow' (place a throw pillow
    // onto the sofa/chair). Pillow uses the hero image as the source + a fabric.
    category: trigger.dataset.category === 'pillow' ? 'pillow' : 'wallpaper',
    fabric: trigger.dataset.fabric === 'twill' ? 'twill' : 'velvet',
    patternColor: '#b8963e',   // procedural fallback tones (only if no asset)
    accentColor: '#8a6f2a',
  };

  // ── Visibility gate ────────────────────────────────────────────────────────
  // The visualizer only appears when BOTH are true:
  //   (a) this design has a generated pattern asset — its SKU stem is present in
  //       bbv-patterns.json, which the TIF→asset pipeline writes ONLY for designs
  //       whose master TIF exists. No TIF → not in the map → hidden on the store.
  //   (b) the render engine is healthy — /health returns ok AND keyPresent.
  // Either failing hides the whole promo so a broken/asset-less visualizer never
  // shows. We pre-hide while the checks run so nothing flashes, then reveal.
  const gateHost = trigger.closest('.bbv-promo') || document.querySelector('.bbv-root');
  const useAssetGate = !!(overlay.dataset.patterns && overlay.dataset.sku);
  const useEngineGate = !!RENDER_ENDPOINT;
  let assetOk = !useAssetGate, engineOk = !useEngineGate;
  let assetDone = !useAssetGate, engineDone = !useEngineGate;
  function applyGate() {
    if (!(assetDone && engineDone)) return;
    const ready = assetOk && engineOk;
    if (gateHost) gateHost.style.display = ready ? '' : 'none';
    // The floating product-page card (bb-pdp-viz-card) waits for this answer: it
    // shows only on '1', and never offers a visualizer that was just hidden.
    overlay.dataset.bbvReady = ready ? '1' : '0';
  }
  if (gateHost && (useAssetGate || useEngineGate)) gateHost.style.display = 'none';

  if (useAssetGate) {
    fetch(overlay.dataset.patterns).then((r) => r.json()).then((map) => {
      const e = map && map[overlay.dataset.sku];
      if (e) { assetOk = true; CONFIG.type = e.type || 'repeat'; if (e.w) CONFIG.repeatInches = Number(e.w); if (e.h) CONFIG.repeatHInches = Number(e.h); }
      else assetOk = false;
    }).catch(() => { assetOk = false; }).finally(() => { assetDone = true; applyGate(); });
  }
  if (useEngineGate) {
    fetch(API_BASE + '/health').then((r) => r.json()).then((j) => {
      engineOk = !!(j && j.ok && j.keyPresent);
    }).catch(() => { engineOk = false; }).finally(() => { engineDone = true; applyGate(); });
  }
  applyGate();

  // Brand mark for the photo watermark. crossOrigin lets us draw the CDN-served
  // asset to the canvas without tainting it, so Download keeps working.
  const markImg = new Image();
  let markReady = false;
  markImg.onload = () => { markReady = true; };
  markImg.crossOrigin = 'anonymous';
  markImg.src = overlay.dataset.mark || '';

  // The product's real pattern — a theme asset built from the 03_tif master
  // (seamless tile for repeats, full design for murals). Falls back to the
  // Shopify product image, then to the procedural pattern. crossOrigin keeps
  // the canvas exportable.
  const patternImg = new Image();
  let patternReady = false;
  patternImg.crossOrigin = 'anonymous';
  patternImg.onload = () => { patternReady = true; };
  patternImg.onerror = () => {
    const fb = overlay.dataset.patternFb;
    if (fb && patternImg.src !== fb) { patternReady = false; patternImg.src = fb; }
  };
  if (overlay.dataset.pattern) patternImg.src = overlay.dataset.pattern;

  // Recolor the (dark) mark to white once, at its NATURAL size (so it never
  // distorts). source-in keeps the mark's alpha, repaints it white. Cached.
  let _whiteMark = null;
  function getWhiteMark() {
    if (_whiteMark) return _whiteMark;
    const c = document.createElement('canvas');
    c.width = markImg.naturalWidth; c.height = markImg.naturalHeight;
    const cx = c.getContext('2d');
    cx.drawImage(markImg, 0, 0);
    cx.globalCompositeOperation = 'source-in';
    cx.fillStyle = '#fff';
    cx.fillRect(0, 0, c.width, c.height);
    _whiteMark = c;
    return c;
  }

  // Brand watermark, bottom-right — larger, no shadow, true aspect ratio.
  // Baked into the canvas so it carries through to Download. Shared by the
  // real render and the mock.
  function stampWatermark(ctx, w, h) {
    if (!(markReady && markImg.naturalWidth)) return;
    const ar = markImg.naturalWidth / markImg.naturalHeight; // preserve aspect
    const mh = Math.max(40, Math.round(h * 0.10));            // small, discreet
    const mw = Math.round(mh * ar);                           // no squish
    const pad = Math.round(h * 0.035);
    ctx.save();
    ctx.globalAlpha = 0.6;
    ctx.drawImage(getWhiteMark(), w - mw - pad, h - mh - pad, mw, mh);
    ctx.restore();
  }

  // Paint the AI render straight onto the canvas (Nano Banana already applied
  // the wallpaper to the room), then stamp the watermark.
  function paintFull(canvas, img) {
    const maxW = 1664; // cap the downloadable preview width (keeps file size reasonable)
    const scale = Math.min(1, maxW / img.naturalWidth);
    const w = Math.round(img.naturalWidth * scale);
    const h = Math.round(img.naturalHeight * scale);
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, w, h);
    stampWatermark(ctx, w, h);
  }

  // ── Modal open / close ───────────────────────────────────────────────────
  let lastFocus = null;
  function openModal() {
    lastFocus = document.activeElement;
    // Lead with the QR hand-off on desktop (room photos live on the phone); a
    // phone visitor — or a setup with no render service — opens straight to the
    // local uploader instead.
    const coarse = isTouchDevice();
    if (API_BASE && !coarse) startPhoneFlow();
    else showStep('upload');
    overlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    ensureTurnstile();
  }
  function closeModal() {
    stopPhoneFlow();
    overlay.classList.remove('is-open');
    document.body.style.overflow = '';
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  // ── Step machine ───────────────────────────────────────────────────────────
  function showStep(name) {
    overlay.querySelectorAll('.step').forEach((s) => {
      s.classList.toggle('is-active', s.dataset.step === name);
    });
  }

  // No local mock: if the live render doesn't come back from the API we show an
  // honest "temporarily unavailable" screen rather than a fake composite that
  // could be mistaken for the real preview. Retry sends them back to upload.
  function showError() { stopPhoneFlow(); showStep('error'); }

  // ── Upload handling ────────────────────────────────────────────────────────
  const drop = $('bbvDrop');
  const fileInput = $('bbvFile');

  function handleFile(file) {
    if (!file) return;
    // Allow files whose MIME type is missing — iPad "High Efficiency" HEIC photos
    // sometimes arrive with an empty type; only reject a type we KNOW isn't an image.
    if (file.type && !/^image\//.test(file.type)) return;
    prepareUpload(file, 1600, (small) => { if (small) startRun(small); else showError(); });
  }

  // Map a pixel w×h to Gemini's nearest supported output aspect ratio (by
  // perceptual log-distance), so the render keeps the room's orientation.
  function nearestAspect(w, h) {
    if (!w || !h) return '';
    const r = w / h;
    const opts = [['1:1', 1], ['3:4', 0.75], ['4:3', 1.3333], ['9:16', 0.5625],
      ['16:9', 1.7778], ['2:3', 0.6667], ['3:2', 1.5], ['4:5', 0.8], ['5:4', 1.25]];
    let best = opts[0], bd = Infinity;
    for (const o of opts) { const d = Math.abs(Math.log(r / o[1])); if (d < bd) { bd = d; best = o; } }
    return best[0];
  }

  // Decode a picked file, shrink it to maxDim on its longest side, and ALWAYS
  // re-encode to JPEG before we send it to the render service. Two iPad-specific
  // reasons this matters:
  //   1. iPad "High Efficiency" photos are HEIC. Gemini can't read HEIC, so we
  //      must transcode to JPEG here (the old code returned the original bytes
  //      unchanged when the image was already small — shipping raw HEIC → error).
  //   2. Safari decodes HEIC reliably from a blob/object URL but NOT always from a
  //      data: URL, so we load the Image from URL.createObjectURL(file), not from
  //      a FileReader data URL. (Data-URL decode is the last-ditch fallback.)
  // Caps upload size + Gemini input cost too. cb(null) on total failure.
  function drawToJpeg(im, maxDim, cb) {
    try {
      CONFIG.aspectRatio = nearestAspect(im.naturalWidth, im.naturalHeight);
      const side = Math.max(im.naturalWidth, im.naturalHeight) || maxDim;
      const scale = side > maxDim ? maxDim / side : 1;
      const c = document.createElement('canvas');
      c.width = Math.max(1, Math.round(im.naturalWidth * scale));
      c.height = Math.max(1, Math.round(im.naturalHeight * scale));
      c.getContext('2d').drawImage(im, 0, 0, c.width, c.height);
      cb(c.toDataURL('image/jpeg', 0.85));
    } catch (e) { cb(null); }
  }
  function prepareUpload(file, maxDim, cb) {
    let url = null;
    try { url = URL.createObjectURL(file); } catch (e) { url = null; }
    const done = (out) => { if (url) { try { URL.revokeObjectURL(url); } catch (e) {} } cb(out); };
    const im = new Image();
    im.onload = () => drawToJpeg(im, maxDim, done);
    im.onerror = () => {
      // Object-URL decode failed — last-ditch attempt via a FileReader data URL.
      try {
        const rd = new FileReader();
        rd.onload = (e) => {
          const im2 = new Image();
          im2.onload = () => drawToJpeg(im2, maxDim, done);
          im2.onerror = () => done(null);
          im2.src = e.target.result;
        };
        rd.onerror = () => done(null);
        rd.readAsDataURL(file);
      } catch (e) { done(null); }
    };
    if (url) im.src = url; else im.onerror();
  }

  fileInput.addEventListener('change', (e) => handleFile(e.target.files && e.target.files[0]));
  ['dragenter', 'dragover'].forEach((ev) =>
    drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.add('is-drag'); }));
  ['dragleave', 'drop'].forEach((ev) =>
    drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.remove('is-drag'); }));
  drop.addEventListener('drop', (e) => {
    const f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
    handleFile(f);
  });

  // Match the preview boxes (working + result) to the uploaded photo's own
  // aspect ratio so the render is never cropped or stretched into a fixed 4:3.
  // The CSS reads --bbv-ar (falls back to 4/3 before any upload).
  function setPreviewRatio(src) {
    const im = new Image();
    im.onload = () => {
      if (im.naturalWidth && im.naturalHeight) {
        overlay.style.setProperty('--bbv-ar', im.naturalWidth + ' / ' + im.naturalHeight);
      }
    };
    im.src = src;
  }

  // ── The run ──────────────────────────────────────────────────────────────
  // API-only: the live render service is the single source of a preview. If no
  // endpoint is configured (or it fails), we show the honest error screen — we
  // never fabricate a local composite that could pass for a real render.
  function startRun(dataUrl) {
    stopPhoneFlow(); // a local upload cancels any in-progress phone hand-off
    setPreviewRatio(dataUrl); // size the working/result boxes to the photo's own ratio
    $('bbvWorkImg').src = dataUrl;
    $('bbvBaseImg').src = dataUrl;
    $('bbvBar').dataset.done = '0';
    showStep('working');
    if (RENDER_ENDPOINT) runViaApi(dataUrl);
    else showError();
  }

  // Build a "scale reference": the room with the pattern tiled at its TRUE size
  // (the design's repeat inches, scaled from the measured wall height assuming a
  // ~108in ceiling) over the wall band. The render service's GUIDED mode copies
  // this scale, so the wallpaper lands at the correct real-world size every time.
  function buildReference(dataUrl, ceilingY, floorY, cb) {
    try {
      const room = new Image();
      room.onload = () => {
        const w = room.naturalWidth, h = room.naturalHeight;
        if (!(patternReady && patternImg.naturalWidth)) return cb(null);
        const c = document.createElement('canvas'); c.width = w; c.height = h;
        const ctx = c.getContext('2d');
        ctx.drawImage(room, 0, 0, w, h);
        const y0 = Math.round(ceilingY * h), y1 = Math.round(floorY * h);
        const pw = patternImg.naturalWidth, ph = patternImg.naturalHeight;
        if (CONFIG.type === 'mural') {
          // Murals fill the wall ONCE (no tiling): cover-fit the whole mural into
          // the wall band, clipped to it. The guided render keeps it as one scene.
          const bw = w, bh = Math.max(1, y1 - y0);
          const s = Math.max(bw / pw, bh / ph);
          const dw = pw * s, dh = ph * s, dx = (bw - dw) / 2, dy = y0 + (bh - dh) / 2;
          ctx.save(); ctx.beginPath(); ctx.rect(0, y0, bw, bh); ctx.clip();
          ctx.drawImage(patternImg, dx, dy, dw, dh); ctx.restore();
        } else {
          // Repeats tile at the design's TRUE size (from the measured wall height,
          // assuming a ~108in ceiling). WIDTH and HEIGHT are tiled SEPARATELY so a
          // non-square repeat (e.g. 72in wide × 108in tall) keeps its real proportions
          // instead of being squished into squares — a 72×108 design then reads as
          // two repeats across a 144in (4-roll) wall, one repeat floor-to-ceiling.
          const pxPerInch = Math.max(1, (floorY - ceilingY) * h) / 108;
          const repW = Math.max(24, Math.round(pxPerInch * (Number(CONFIG.repeatInches) || 36)));
          const repH = Math.max(24, Math.round(pxPerInch * (Number(CONFIG.repeatHInches) || 108)));
          for (let y = y0; y < y1; y += repH) {
            for (let x = 0; x < w; x += repW) ctx.drawImage(patternImg, 0, 0, pw, ph, x, y, repW, repH);
          }
        }
        try { cb(c.toDataURL('image/jpeg', 0.9)); } catch (e) { cb(null); }
      };
      room.onerror = () => cb(null);
      room.src = dataUrl;
    } catch (e) { cb(null); }
  }

  // Real render. Default path = GUIDED (correct real-world scale): measure the
  // wall → tile a scale reference → the model matches that scale, segments the
  // furniture, and relights. Falls back to DIRECT placement for murals, or on any
  // failure (wall not found / guided error / network).
  function runViaApi(dataUrl) {
    const bar = $('bbvBar');
    bar.style.width = '0%';
    let settled = false;
    const start = performance.now();
    (function tick(now) {
      if (settled) return;
      const p = Math.min(1, (now - start) / 13000);
      bar.style.width = (92 * (1 - Math.pow(1 - p, 2))).toFixed(1) + '%';
      requestAnimationFrame(tick);
    })(start);
    const finish = (src) => { settled = true; bar.style.width = '100%'; drawResult(src); };

    const roomB64 = dataUrl.split(',')[1] || '';
    const roomMime = (dataUrl.match(/^data:(.*?);/) || [])[1] || 'image/jpeg';
    const post = (payload) => fetch(RENDER_ENDPOINT, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    }).then((r) => r.json().then((j) => ({ ok: r.ok, j })));

    // Direct placement — model tiles + scales on its own (fallback / murals).
    const direct = () => post({
      roomB64, roomMime, patternUrl: absUrl(overlay.dataset.pattern), patternFb: absUrl(overlay.dataset.patternFb),
      sku: overlay.dataset.sku || '', type: CONFIG.type, repeatInches: CONFIG.repeatInches,
      sizeLabel: CONFIG.sizeLabel, productTitle: CONFIG.productName, aspectRatio: CONFIG.aspectRatio || '',
      turnstileToken: getTurnstileToken(),
    }).then(({ ok, j }) => {
      if (!ok || !j || !j.ok || !j.imageB64) throw new Error((j && j.error) || 'render_failed');
      LAST_RENDER_ID = (j && j.renderId) || null;
      finish('data:' + (j.mime || 'image/png') + ';base64,' + j.imageB64, false);
    }).catch(() => showError());

    // Pillow placement — send the room + the pillow's hero image; the Worker
    // places it as a realistic 24" cushion (mode:'pillow', fabric picks texture).
    // No wall measurement / scale reference — the pillow is an object, not a tile.
    const pillow = () => post({
      roomB64, roomMime, patternUrl: absUrl(overlay.dataset.pattern),
      mode: 'pillow', category: 'pillow', type: 'pillow', fabric: CONFIG.fabric,
      sku: overlay.dataset.sku || '', productTitle: CONFIG.productName,
      aspectRatio: CONFIG.aspectRatio || '', turnstileToken: getTurnstileToken(),
    }).then(({ ok, j }) => {
      if (!ok || !j || !j.ok || !j.imageB64) throw new Error((j && j.error) || 'render_failed');
      LAST_RENDER_ID = (j && j.renderId) || null;
      finish('data:' + (j.mime || 'image/png') + ';base64,' + j.imageB64);
    }).catch(() => showError());

    // Murals go DIRECT (the real mural image + a "preserve the artwork exactly"
    // prompt + the strong model) — the guided cover-fit reference made the model
    // drop figurative subjects. Repeats go guided (correct scale). Failure → direct.
    // Wait for the Turnstile token before any render POST, so a fast upload isn't
    // 403'd and bounced to the local mock. ensureTurnstile() is idempotent; the
    // widget was already started on modal open, so this usually resolves instantly.
    ensureTurnstile();
    waitForTurnstileToken(6000).then(() => {
      if (CONFIG.category === 'pillow') { pillow(); return; }
      if (CONFIG.type === 'mural' || !patternReady) { direct(); return; }

      // Guided: measure wall → reference at true scale → guided render.
      fetch(API_BASE + '/wall', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ roomB64, roomMime }),
      }).then((r) => r.json()).then((g) => {
        if (!g || !g.ok || !g.found) return direct();
        buildReference(dataUrl, g.ceilingY, g.floorY, (refUrl) => {
          if (!refUrl) return direct();
          post({
            roomB64, roomMime, patternB64: (refUrl.split(',')[1] || ''), patternMime: 'image/jpeg',
            mode: 'guided', sku: overlay.dataset.sku || '', type: CONFIG.type,
            productTitle: CONFIG.productName, aspectRatio: CONFIG.aspectRatio || '', turnstileToken: getTurnstileToken(),
          }).then(({ ok, j }) => {
            if (!ok || !j || !j.ok || !j.imageB64) throw new Error((j && j.error) || 'guided_failed');
            LAST_RENDER_ID = (j && j.renderId) || null;
            finish('data:' + (j.mime || 'image/png') + ';base64,' + j.imageB64, false);
          }).catch(() => direct()); // guided failed → direct
        });
      }).catch(() => direct()); // wall measure failed → direct
    });
  }

  // Draw the final (live API) render and reveal it. `mock` is retained only for
  // call-signature compatibility and is always false — there is no local mock.
  let lastResultCanvas = null;
  function drawResult(src) {
    const img = new Image();
    img.onload = () => {
      const canvas = $('bbvCanvas');
      // Lock the result box to the rendered image's true ratio so the composite
      // fills the frame exactly — no crop, stretch, or bars.
      if (img.naturalWidth && img.naturalHeight) {
        overlay.style.setProperty('--bbv-ar', img.naturalWidth + ' / ' + img.naturalHeight);
      }
      paintFull(canvas, img);
      lastResultCanvas = canvas;
      try { $('bbvDownload').href = canvas.toDataURL('image/jpeg', 0.9); } catch (e) { /* tainted → no download */ }
      resetFeedback();
      showStep('result');
      const reveal = $('bbvReveal');
      reveal.classList.remove('is-revealed');
      reveal.classList.add('is-revealing');
      requestAnimationFrame(() => requestAnimationFrame(() => reveal.classList.add('is-revealed')));
      setTimeout(() => reveal.classList.remove('is-revealing'), 1300);
    };
    img.onerror = () => { showError(); };
    img.src = src;
  }

  // ── Phone hand-off (QR → upload on phone → result lands back here) ─────────
  // The desktop creates a pairing, shows a QR of the mobile uploader, and polls
  // until the phone's render comes back — then drops it onto the same result
  // screen as a local upload.
  let pairId = null, pollTimer = null, pollDeadline = 0;

  // Crisp, scalable SVG QR for `text` (Byte mode; smallest type that fits).
  function qrSvg(text) {
    if (typeof window.qrcode !== 'function') return '';
    let qr = null;
    for (let t = 3; t <= 20 && !qr; t++) {
      try { const q = window.qrcode(t, 'M'); q.addData(text); q.make(); qr = q; } catch (e) { /* too small → grow */ }
    }
    if (!qr) return '';
    const n = qr.getModuleCount(), pad = 2, size = n + pad * 2;
    let rects = '';
    for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) {
      if (qr.isDark(r, c)) rects += '<rect x="' + (c + pad) + '" y="' + (r + pad) + '" width="1" height="1"/>';
    }
    return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + size + ' ' + size + '" shape-rendering="crispEdges">' +
      '<rect width="' + size + '" height="' + size + '" fill="#fff"/><g fill="#2a1f1a">' + rects + '</g></svg>';
  }

  // Ease the working bar toward ~92% while the phone render is in flight.
  function animatePhoneBar(durationMs) {
    const bar = $('bbvBar'); bar.style.width = '0%';
    const start = performance.now();
    (function tick(now) {
      if (!pairId || Number(bar.dataset.done) === 1) return;
      const p = Math.min(1, (now - start) / durationMs);
      bar.style.width = (92 * (1 - Math.pow(1 - p, 2))).toFixed(1) + '%';
      requestAnimationFrame(tick);
    })(start);
  }

  function startPhoneFlow() {
    if (!API_BASE) return;
    const qr = $('bbvQr'); qr.innerHTML = '';
    $('bbvQrWait').innerHTML = '<span class="bbv-qr-dot"></span> Preparing your code…';
    showStep('phone');
    fetch(API_BASE + '/pair', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sku: overlay.dataset.sku || '',
        patternUrl: absUrl(overlay.dataset.pattern),
        patternFb: absUrl(overlay.dataset.patternFb),
        type: CONFIG.type,
        category: CONFIG.category,
        fabric: CONFIG.fabric,
        repeatInches: CONFIG.repeatInches,
        repeatHInches: CONFIG.repeatHInches,
        sizeLabel: CONFIG.sizeLabel,
        productTitle: CONFIG.productName,
        turnstileSitekey: TS_SITEKEY,
      }),
    })
      .then((r) => r.json())
      .then((j) => {
        if (!j || !j.ok || !j.mobileUrl) throw new Error('pair_failed');
        const svg = qrSvg(j.mobileUrl);
        qr.innerHTML = svg || ('<a href="' + j.mobileUrl + '">Open uploader</a>');
        $('bbvQrWait').innerHTML = '<span class="bbv-qr-dot"></span> Waiting for your phone…';
        pairId = j.pairId;
        pollDeadline = performance.now() + 14 * 60 * 1000; // poll up to ~14 min
        schedulePoll(1200);
      })
      .catch(() => { $('bbvQrWait').textContent = "Couldn't start the phone hand-off — please upload here instead."; });
  }

  function schedulePoll(delay) {
    clearTimeout(pollTimer);
    pollTimer = setTimeout(pollPair, delay);
  }

  function pollPair() {
    if (!pairId) return;
    if (performance.now() > pollDeadline) {
      $('bbvQrWait').textContent = 'This code expired — use this computer, or reopen for a fresh code.';
      return;
    }
    fetch(API_BASE + '/pair/' + pairId)
      .then((r) => r.json())
      .then((j) => {
        const st = j && j.status;
        if (st === 'ready' && j.imageB64) {
          $('bbvBar').dataset.done = '1'; $('bbvBar').style.width = '100%';
          stopPhoneFlow();
          LAST_RENDER_ID = j.renderId || null;
          drawResult('data:' + (j.mime || 'image/png') + ';base64,' + j.imageB64, false);
        } else if (st === 'rendering') {
          const working = overlay.querySelector('.step[data-step="working"]');
          if (working && !working.classList.contains('is-active')) {
            $('bbvWorkImg').src = overlay.dataset.pattern || overlay.dataset.patternFb || '';
            $('bbvBar').dataset.done = '0';
            showStep('working');
            animatePhoneBar(12000);
          }
          schedulePoll(1800);
        } else if (st === 'error') {
          showError();
        } else if (st === 'expired') {
          $('bbvQrWait').textContent = 'This code expired — use this computer, or reopen for a fresh code.';
        } else {
          schedulePoll(1500); // waiting for the phone
        }
      })
      .catch(() => schedulePoll(3000));
  }

  function stopPhoneFlow() {
    clearTimeout(pollTimer);
    pollTimer = null; pairId = null; pollDeadline = 0;
  }

  // ── Result actions (order/sample wire up with the live store) ──────────────
  function toast(msg) {
    const t = $('bbvToast');
    t.textContent = msg; t.classList.add('show');
    clearTimeout(toast._t); toast._t = setTimeout(() => t.classList.remove('show'), 2200);
  }
  // Mobile-friendly download: iOS Safari ignores <a download> for data: URLs. On
  // touch devices, share the image (native Save-Image sheet) or open it in a new tab
  // (long-press → Save Image). Desktop keeps the plain <a download href>.
  $('bbvDownload').addEventListener('click', (e) => {
    sendEvent('download'); // before the desktop early-return below, or desktop never counts
    const cv = lastResultCanvas;
    const coarse = isTouchDevice();
    if (!cv || !coarse) return; // desktop → let the default download proceed
    e.preventDefault();
    cv.toBlob((blob) => {
      if (blob && navigator.canShare) {
        const file = new File([blob], 'buffalo-bunny-preview.jpg', { type: 'image/jpeg' });
        if (navigator.canShare({ files: [file] })) { navigator.share({ files: [file], title: 'Buffalo Bunny preview' }).catch(() => {}); return; }
      }
      try { const u = URL.createObjectURL(blob); window.open(u, '_blank'); setTimeout(() => URL.revokeObjectURL(u), 60000); }
      catch (er) { try { window.open(cv.toDataURL('image/jpeg', 0.9), '_blank'); } catch (e2) {} }
    }, 'image/jpeg', 0.9);
  });
  // "Order" closes the visualizer and scrolls the shopper to the product's buy section.
  $('bbvOrder').addEventListener('click', () => {
    sendEvent('order_click');
    closeModal();
    const buy = document.querySelector('product-form, .shopify-product-form, [id^="product-form"], .product-form, [name="add"]');
    if (buy) buy.scrollIntoView({ behavior: 'smooth', block: 'center' });
    else window.scrollTo({ top: 0, behavior: 'smooth' });
  });

  // ── Wiring ──────────────────────────────────────────────────────────────────
  trigger.addEventListener('click', openModal);

  // Deep-link entry: a "See it in your space" CTA elsewhere (e.g. the home page)
  // can link to this product with ?visualize=1 or #see-it to open the visualizer on
  // arrival. openModal() picks the right step per device (QR on desktop, upload on
  // mobile). Small delay lets the page + Turnstile settle first.
  try {
    if (/[?&]visualize=1(&|$)/.test(location.search) || location.hash === '#see-it') {
      setTimeout(openModal, 250);
    }
  } catch (e) { /* no-op */ }

  // Offer the phone hand-off only when a real render service is configured and
  // we're likely on a desktop (a phone visitor can just upload directly).
  const usePhoneBtn = $('bbvUsePhone');
  if (usePhoneBtn) {
    const coarse = isTouchDevice();
    if (API_BASE && !coarse) usePhoneBtn.hidden = false;
    usePhoneBtn.addEventListener('click', startPhoneFlow);
  }
  const phoneBackBtn = $('bbvPhoneBack');
  if (phoneBackBtn) phoneBackBtn.addEventListener('click', () => { stopPhoneFlow(); showStep('upload'); });

  const retryBtn = $('bbvRetry');
  if (retryBtn) retryBtn.addEventListener('click', () => { resetTurnstile(); showStep('upload'); });

  $('bbvClose').addEventListener('click', closeModal);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) closeModal(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && overlay.classList.contains('is-open')) closeModal(); });

  // Phone hand-off: while the shopper is on their phone, this desktop tab is in
  // the background, so the browser throttles its poll timers and a finished
  // render can sit unseen. The moment the desktop regains focus/visibility, poll
  // right away so the result appears immediately instead of after a throttle gap.

  // ── "Doesn't look right?" — the quality signal ──────────────────────────────
  // Until 2026-09-11 the ONLY way a bad render surfaced was Eric happening to
  // look at one; ~100 renders had gone out unseen when a mural came back with a
  // 3D bubble that is not in the artwork. The person looking at their own room
  // is the best judge we will ever have, so the result screen asks them.
  //
  // Two taps, no typing: open, then pick what is wrong. We send a CATEGORY, never
  // free text. Fails silently in every direction — a dead signal must never cost
  // the customer their preview.
  // Implicit signal: what the viewer DID with the render. Costs no UI and hears
  // from everyone, not just the people annoyed enough to tap the flag.
  // ⚠️ 'order_click' is a click-through to the buy form, NOT a purchase.
  function sendEvent(name) {
    if (!LAST_RENDER_ID || !API_BASE) return;
    try {
      fetch(API_BASE + '/event', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ renderId: LAST_RENDER_ID, event: name }),
        keepalive: true, // the Order click closes the modal and scrolls away
      }).catch(() => {});
    } catch (e) { /* never let a signal break the button */ }
  }

  function resetFeedback() {
    const w = $('bbvFb');
    if (!w) return;
    w.classList.remove('is-open', 'is-done');
    w.hidden = !LAST_RENDER_ID; // no id (older Worker / failed render) → no control
  }

  (function wireFeedback() {
    const w = $('bbvFb');
    if (!w) return;
    const open = w.querySelector('.bbv-fb__open');
    if (open) open.addEventListener('click', () => w.classList.add('is-open'));
    w.querySelectorAll('.bbv-fb__chip').forEach((btn) => {
      btn.addEventListener('click', () => {
        const reason = btn.dataset.reason || 'other';
        w.classList.add('is-done');
        if (!LAST_RENDER_ID || !API_BASE) return;
        fetch(API_BASE + '/feedback', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ renderId: LAST_RENDER_ID, verdict: 'bad', reason }),
        }).catch(() => { /* the thank-you already showed; never bother the customer */ });
      });
    });
  })();

  function pollNow() { if (pairId) { clearTimeout(pollTimer); pollDeadline = Math.max(pollDeadline, performance.now() + 8000); pollPair(); } }
  document.addEventListener('visibilitychange', () => { if (!document.hidden) pollNow(); });
  window.addEventListener('focus', pollNow);
})();
